import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router, UrlTree } from '@angular/router';
import { filter } from 'rxjs';

const JOB_PAGE = /^\/embed\/jobs\/[^/]+(\/|$)/;
const SCHOOL_PROFILE_PAGE = /^\/embed\/schools\/[^/]+$/;

/**
 * Keeps a visitor inside the customer's iframe (`/embed/...`): job detail and the
 * public apply flow render under EmbedLayout too, so the customer's website stays
 * around them. Only the owner's school profile may leave — and only when the
 * customer did not integrate school profiles themselves.
 *
 * Must be created before the first NavigationEnd, which is why EmbedLayoutComponent
 * injects it.
 */
@Injectable({ providedIn: 'root' })
export class EmbedNavigationService {
  private readonly router = inject(Router);

  /**
   * Last embed page that is not a job page — where "Zurück" returns to. Never
   * `history.back()`: inside an iframe that can navigate the customer's page.
   */
  private returnUrl: string | null = null;
  /** A `/embed/schools/:id` profile was rendered in this iframe session. */
  private schoolProfileShown = false;

  constructor() {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => this.record(e.urlAfterRedirects));
  }

  /**
   * The list or profile the visitor came from, filters and scroll position
   * included. `null` when the iframe was opened directly on a job.
   */
  get backUrl(): UrlTree | null {
    return this.returnUrl ? this.router.parseUrl(this.returnUrl) : null;
  }

  jobLink(jobId: string): string[] {
    return ['/embed/jobs', jobId];
  }

  /**
   * Whether the owner's profile can open inside the iframe. Visitors only notice
   * they left the customer's site when the customer did not embed school profiles.
   */
  keepsSchoolProfileInEmbed(): boolean {
    return this.schoolProfileShown;
  }

  private record(url: string): void {
    const path = url.split(/[?#]/)[0];
    if (!path.startsWith('/embed/')) {
      return;
    }
    if (SCHOOL_PROFILE_PAGE.test(path)) {
      this.schoolProfileShown = true;
    }
    if (!JOB_PAGE.test(path)) {
      this.returnUrl = url;
    }
  }
}
